/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 *
 * Excel Service
 *
 * Converts an uploaded Excel file into a `PassportDto` using a version-aware
 * `ExcelParserConfig`. Handles template validation, header skipping, configured
 * value mapping, and deep-path property assignment into the passport data model.
 *
 * Key Features:
 * - Version string validation against the first cell of the Excel file
 * - Configurable header row count, key column, and value column indices
 * - Configured value mappings for cells that need raw values or structured payloads
 * - Deep dotted-path property assignment via `GeneralDataHandlingService`
 *
 * Related Modules:
 * - `ExcelParserConfig` / `EXCEL_CONFIGS` in `Constants.ts`
 * - `GeneralDataHandlingService` for dotted-path value assignment
 * - `ModalCreateBatteryPassComponent` — primary consumer of this service
 */

import {Injectable, inject} from '@angular/core';
import readXlsxFile from 'read-excel-file';
import {ExcelParserConfig} from '../models/Constants';
import {GeneralDataHandlingService} from './general.data-handling.service';
import {PassportDto} from '../models/Passport';

function cloneMappedValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return [...value];
  }

  if (value !== null && typeof value === 'object') {
    return {...value};
  }

  return value;
}

/**
 * Parses an Excel passport template and maps its contents into a `PassportDto`.
 *
 * This service is version-aware: each `ExcelParserConfig` carries a `versionString`
 * that must match the first cell of the uploaded file, ensuring templates are not
 * silently misread after format changes.
 */
@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  private readonly generalDataHandlingService = inject(
    GeneralDataHandlingService,
  );

  /**
   * Reads an Excel passport template and writes its field values into `passportDto`.
   *
   * The method delegates raw row extraction to `readExcelWithConfig`, then iterates
   * over the resulting key/value pairs. For each non-empty key it optionally applies
   * a configured value mapping before calling `GeneralDataHandlingService.setValueByPath`
   * to write the value at the dotted path encoded in the key cell.
   *
   * Example Usage:
   * ```typescript
   * const updated = await excelService.excelToPassport(file, EXCEL_CONFIGS['BP_1.2.0'], dto);
   * ```
   *
   * @param {File} excelFile - The `.xlsx` file uploaded by the user.
   * @param {ExcelParserConfig} config - Version-specific parser configuration (row offsets, column indices, enum maps).
   * @param {PassportDto} passportDto - The passport DTO to be populated; mutated in place and returned.
   * @returns {Promise<PassportDto>} The same `passportDto` reference with fields populated from the Excel file.
   * @throws {Error} `'InvalidTemplate'` when the file contains no rows.
   * @throws {Error} `'InvalidVersion'` when the first cell does not match `config.versionString`.
   */
  public async excelToPassport(
    excelFile: File,
    config: ExcelParserConfig,
    passportDto: PassportDto,
  ): Promise<PassportDto> {
    const excelData = await this.readExcelWithConfig(excelFile, config);

    excelData.keys.forEach((key, index) => {
      if (!key) {
        return;
      }

      let value: unknown = excelData.values[index];

      // Replace the Excel cell value when a mapping exists for this field.
      const valueMappings = config.valueMappings[key];
      if (
        valueMappings &&
        typeof value === 'string' &&
        Object.prototype.hasOwnProperty.call(valueMappings, value)
      ) {
        const mappedValue = valueMappings[value];
        value = cloneMappedValue(mappedValue);
      }

      this.generalDataHandlingService.setValueByPath(
        passportDto.data,
        key,
        value,
      );
    });

    return passportDto;
  }

  /**
   * Reads raw rows from the Excel file and extracts parallel key/value arrays
   * according to the column indices and header row count defined in `config`.
   *
   * Header rows (containing the version string and column labels) are included
   * during collection so that column alignment is preserved, then stripped via
   * `Array.shift()` before the arrays are returned.
   *
   * @param {File} excelFile - The `.xlsx` file to parse.
   * @param {ExcelParserConfig} config - Parser configuration with row/column offsets and version string.
   * @returns {Promise<{keys: string[]; values: string[]}>} Parallel arrays of field path keys and their corresponding cell values.
   * @throws {Error} `'InvalidTemplate'` when the file is empty.
   * @throws {Error} `'InvalidVersion'` when `rows[0][0]` does not equal `config.versionString`.
   */
  private async readExcelWithConfig(
    excelFile: File,
    config: ExcelParserConfig,
  ): Promise<{keys: string[]; values: string[]}> {
    const keys: Array<string> = [];
    const values: Array<string> = [];

    const rows = await readXlsxFile(excelFile);

    if (rows.length === 0) {
      throw new Error('InvalidTemplate');
    }

    if (rows[0][0] !== config.versionString) {
      throw new Error('InvalidVersion');
    }

    // Collect all rows including headers; non-null value cells are always included.
    let rowCounter = 0;
    rows.forEach((row) => {
      rowCounter++;
      if (
        rowCounter <= config.headerLines ||
        (row[config.valuesColumn] !== null &&
          row[config.valuesColumn] !== undefined)
      ) {
        keys.push(row[config.keysColumn] as string);
        values.push(row[config.valuesColumn] as string);
      }
    });

    // Strip the header rows that were collected purely to maintain index alignment.
    for (let i = 0; i < config.headerLines; i++) {
      keys.shift();
      values.shift();
    }

    return {keys, values};
  }
}
